import {
  HttpVerb,
  IGenerateGiftCardBatchDTO,
  IGiftCardBatchDTO,
  IGiftCardDTO,
  IGiftCardTypeDTO,
  IGiftCardsController,
  IRedeemGiftCardRequestDTO,
  IRouteDefinition,
  ISellGiftCardsRequestDTO,
  IUpsertGiftCardTypeDTO,
} from "@shared/types";
import { EventBaseService } from "./Base/EventBaseService";
import { EventType, Feature } from "./Events/ServiceEvents";

const GetTypesRoute: IRouteDefinition = { url: "GiftCards/types", method: HttpVerb.Get, queryType: "" };
const UpsertTypeRoute: IRouteDefinition = { url: "GiftCards/types", method: HttpVerb.Post, queryType: "" };
const GenerateBatchRoute: IRouteDefinition = { url: "GiftCards/generate", method: HttpVerb.Post, queryType: "" };
const SellRoute: IRouteDefinition = { url: "GiftCards/sell", method: HttpVerb.Post, queryType: "" };
const RedeemRoute: IRouteDefinition = { url: "GiftCards/redeem", method: HttpVerb.Post, queryType: "" };
const GetByCodeRoute: (code: string) => IRouteDefinition = (code) => ({
  url: `GiftCards/${encodeURIComponent(code)}`,
  method: HttpVerb.Get,
  queryType: "",
});

export class GiftCardService extends EventBaseService implements IGiftCardsController {
  private static instance: GiftCardService;

  private constructor() {
    super();
  }

  public static getInstance(): GiftCardService {
    if (!GiftCardService.instance) {
      GiftCardService.instance = new GiftCardService();
    }
    return GiftCardService.instance;
  }

  public getTypes(): Promise<IGiftCardTypeDTO[]> {
    return this.http.sendRequest<IGiftCardTypeDTO[]>(GetTypesRoute);
  }

  public upsertType(dto: IUpsertGiftCardTypeDTO): Promise<IGiftCardTypeDTO> {
    return this.withEventEmission(Feature.GiftCard, EventType.Updated, () =>
      this.http.sendRequest<IGiftCardTypeDTO>(UpsertTypeRoute, { data: dto })
    );
  }

  public generate(dto: IGenerateGiftCardBatchDTO): Promise<IGiftCardBatchDTO> {
    return this.withEventEmission(Feature.GiftCard, EventType.Added, () =>
      this.http.sendRequest<IGiftCardBatchDTO>(GenerateBatchRoute, { data: dto })
    );
  }

  public getBatches(query: Partial<typeof IGiftCardsController.IGetBatchesQuery> = {}): Promise<IGiftCardBatchDTO[]> {
    return this.http.sendRequest<IGiftCardBatchDTO[]>(IGiftCardsController.GetBatchesRoute, { query });
  }

  public getGiftCards(
    query: Partial<typeof IGiftCardsController.IGetGiftCardsQuery> = {}
  ): Promise<IGiftCardDTO[]> {
    return this.http.sendRequest<IGiftCardDTO[]>(IGiftCardsController.GetGiftCardsRoute, { query });
  }

  public exportBatchPdf(query: Partial<typeof IGiftCardsController.IExportBatchPdfQuery> = {}): Promise<Blob> {
    return this.http.sendRequest<Blob>(IGiftCardsController.ExportBatchPdfRoute, {
      query: query,
      config: { responseType: "blob" },
    });
  }

  public sell(dto: ISellGiftCardsRequestDTO): Promise<IGiftCardDTO[]> {
    return this.withEventEmission(Feature.GiftCard, EventType.Updated, () =>
      this.http.sendRequest<IGiftCardDTO[]>(SellRoute, { data: dto })
    );
  }

  public redeem(dto: IRedeemGiftCardRequestDTO): Promise<IGiftCardDTO> {
    return this.withEventEmission(Feature.GiftCard, EventType.Updated, () =>
      this.http.sendRequest<IGiftCardDTO>(RedeemRoute, { data: dto })
    );
  }

  public getByCode(code: string): Promise<IGiftCardDTO> {
    return this.http.sendRequest<IGiftCardDTO>(GetByCodeRoute(code));
  }

}
